import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { GridWithSearch, PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { DeleteListDialog } from 'pages/listManagement/dialogs/DeleteListDialog';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get } from 'services/Http';
import {
    ListSchema,
    ListSchemaApiURLPrefix,
    ListSchemaAppRoutePrefix,
    ListType,
    ListTypeNameMap,
} from 'services/ListManagementHelpers';

const CustomSearchValueHandlers = {
    // Turn list of items into comma delimited string of values
    list_search_data: (value) =>
        value
            ?.map((item) => (typeof item === 'object' ? Object.values(item).join(',') : item))
            .join(','),
};

const getLists = (listSchema, listType, searchText) =>
    get(
        `${ListSchemaApiURLPrefix[listSchema]}/${listType}`,
        searchText ? { search_text: searchText } : undefined,
    );

const columns = ({ listSchema, listType, setListToDelete, navigate }) => [
    {
        header: `${ListTypeNameMap[listType]} List Name`,
        accessorKey: 'list_name',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Last Updated',
        accessorKey: 'last_updated',
        size: 120,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        header: 'Updated By',
        accessorKey: 'upn',
        size: 120,
    },
    {
        header: 'Actions',
        accessorKey: `${listType}_list_id`,
        size: 70,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() =>
                        navigate({
                            pathname: `${ListSchemaAppRoutePrefix[listSchema]}/${listType}/${getValue()}`,
                        })
                    }
                />
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.SECONDARY}
                    title="Delete"
                    onClick={() => setListToDelete(row.original)}
                />
            </div>
        ),
    },
];
export const ListManagement = ({ listSchema }) => {
    const { listType } = useParams();
    const navigate = useNavigate();

    const isListTypeInvalid = !ListType[listType];

    const isBackendFilteredList = listSchema === ListSchema.assignment;
    const [searchText, setSearchText] = useState();

    const [lists, setLists] = useState();
    useEffect(() => {
        void (async () => {
            try {
                setLists(await getLists(listSchema, listType, searchText));
            } catch (err) {
                handleError('Error fetching lists', err);
            }
        })();
        return () => {
            setLists();
        };
    }, [listSchema, listType, searchText]);

    const [listToDelete, setListToDelete] = useState();

    if (isListTypeInvalid) {
        return <Navigate replace to="/home" />;
    }

    return (
        <div className="sds_list-page">
            <div className="sds_main-content__container">
                <DeleteListDialog
                    listSchema={listSchema}
                    listType={listType}
                    listToDelete={listToDelete}
                    onClose={async () => {
                        try {
                            setListToDelete();
                            setLists(await getLists(listSchema, listType, searchText));
                        } catch (err) {
                            handleError('Error fetching lists', err);
                        }
                    }}
                />
                <ListPageHeader
                    pageTitle={`${ListTypeNameMap[listType]} Lists`}
                    buttons={[
                        {
                            label: `Add ${ListTypeNameMap[listType]} List`,
                            path: `${ListSchemaAppRoutePrefix[listSchema]}/${listType}/${CREATE_ROUTE}`,
                        },
                    ]}
                />
                <div className="sds_main-content__grid">
                    <GridWithSearch
                        data={lists}
                        isLoading={!lists}
                        sortProperties={[{ id: 'list_name', desc: true }]}
                        customSearchValueHandlers={CustomSearchValueHandlers}
                        columns={columns({ listSchema, listType, setListToDelete, navigate })}
                        backendSearch={isBackendFilteredList}
                        searchText={isBackendFilteredList ? searchText : undefined}
                        onSearch={isBackendFilteredList ? setSearchText : undefined}
                    />
                </div>
            </div>
        </div>
    );
};

ListManagement.propTypes = {
    listSchema: PropTypes.oneOf(Object.values(ListSchema)).isRequired,
};
